import { Injectable, Type } from '@angular/core';
import { Offer } from '../model/offer';
import { SharedService } from './shared.service';
import { OfferPhotoTermsComponent } from '../components/offer-photo-terms/offer-photo-terms.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ShoppingCartListModalComponent } from '../components/shopping-cart-list-modal/shopping-cart-list-modal.component';
import { OfferCategory } from '../model/offer-category.model';
import { OfferCategoryService } from './offer-category.service';


export interface OfferAliasAcceptedData {
  offerError: boolean;
  accepted: boolean;
  acknowledgementEmailSent: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ShoppingCartService {


  offerIds: {}[] = [];

  public offerSets: { offerSetId: string; offerSet: Offer[] }[] = [{ offerSetId: '', offerSet: [] }]; // Offer[]
  
  public offerAliasId: string = '';
  public propId: string = '';

  public selectedOffers: {
    [offerAliasId: string]: { offerSetId: string; offerId: string; offer: Offer; }[];
  } = {};

  private readonly defaultOfferAliasAcceptedData: OfferAliasAcceptedData = {
    offerError: false,
    accepted: false,
    acknowledgementEmailSent: false,
  };

  constructor(public sharedService: SharedService, 
              private modalService: BsModalService,
              private offerCategoryService: OfferCategoryService) {
  }

  public getNumberOfOffersInShoppingCart(offerAliasId: string): number {
    return this.selectedOffers[offerAliasId]?.length || 0;
  }

  public checkIfEciOfferInShoppingCart() {
      return this.selectedOffers[this.offerAliasId]?.some(set => this.offerCategoryService.isOfferInCategory(set.offer, 'ECI'))
  }

  private isEciOffer(offer: Offer | null | undefined): boolean {
    return this.offerCategoryService.isOfferInCategory(offer, 'ECI');
  }

  private isLcoOffer(offer: Offer | null | undefined): boolean {
    return this.offerCategoryService.isOfferInCategory(offer, 'LCO');
  }

  private isAddonOffer(offer: Offer | null | undefined): boolean {
    return this.offerCategoryService.isOfferInCategory(offer, 'ANCILLARY');
  }

  private isRoomOffer(offer: Offer | null | undefined): boolean {
    if (!offer) return false;
    return !offer.flg_addon && this.offerCategoryService.isOfferInCategory(offer, 'ROOM');
  }

  public hasOffersInCartForTab(
    offerAliasId: string,
    tab: OfferCategory
  ): boolean {
    if (!offerAliasId) return false;
    const selected = this.selectedOffers[offerAliasId] ?? [];

    return selected.some(item => {
      const offer = item.offer;
      if (tab === 'ECI') return this.isEciOffer(offer);
      if (tab === 'LCO') return this.isLcoOffer(offer);
      if (tab === 'ANCILLARY') return this.isAddonOffer(offer);
      return this.isRoomOffer(offer);
    });
  }
  
  public removeOfferInShoppingCartByOfferId(offerAliasId: string, offerId: string): void {
    if (this.selectedOffers[offerAliasId]) {
      this.selectedOffers[offerAliasId] = this.selectedOffers[offerAliasId].filter(
        set => set.offer.offer_id !== offerId
      );
    }
  }


  public removeAllRoomOffersInShoppingCartByAlias(offerAliasId: string): void {
    if (this.selectedOffers[offerAliasId]) {
      this.selectedOffers[offerAliasId] = this.selectedOffers[offerAliasId].filter(
        set => !this.offerCategoryService.isOfferInCategory(set.offer, 'ROOM')
      );
    }
  }


  public removeAllOffersInShoppingCartByCatCd(offerAliasId: string, offerCategory: OfferCategory): void {
    if(this.selectedOffers[offerAliasId]){
      this.selectedOffers[offerAliasId] = this.selectedOffers[offerAliasId].filter(
        set => !this.offerCategoryService.isOfferInCategory(set.offer, offerCategory)
      );
    }
  }

  public addOffer(offerAliasId: string, offerSetId: string, offerId: string, offer: Offer): void {
    if (!offerId) return;
    let exists = false;
    if (this.selectedOffers[offerAliasId])
    {
      exists = this.selectedOffers[offerAliasId]?.some(set => set.offerSetId === offerSetId && set.offerId === offerId);
    } else {
      this.selectedOffers[offerAliasId] = [];
    }

    if (!exists) {
      const newSet = { offerSetId, offerId, offer };
      this.selectedOffers[offerAliasId].push(newSet);
    }
  }

  public removeOffer(offerAliasId: string, offerSetId: string, offerId: string): void {
    if (!offerId) return;
  
    this.selectedOffers[offerAliasId] = this.selectedOffers[offerAliasId].filter(
      set => !(set.offerSetId === offerSetId && set.offerId === offerId)
    );
  }

  public removeOffersByOfferAliasId(offerAliasId: string): void {
    if (!offerAliasId) return;
    this.selectedOffers[offerAliasId] = [];
  }

  public getOffersByAliasId(offerAliasId: string): { offerSetId: string; offerId: string; offer: Offer; }[]{
    return this.selectedOffers[offerAliasId]
  }

  public getOfferByIds(offerAliasId: string, offerSetId: string, offerId: string): Offer | null {
    if(this.selectedOffers[offerAliasId]){
      const found = this.selectedOffers[offerAliasId].find(set => set.offerSetId === offerSetId && set.offerId === offerId);
      if(found && found !== undefined) {
        return found.offer;
      }
    }
    return null;
  }

  public addOfferToStorageByIds(
    offerAliasId: string,
    offerSetId: string,
    offerId: string,
    offer: Offer
  ): void {

    if (!offer) return;
  
    const storageKey = `offer_byIds_${offerAliasId + '|' + offerSetId + '|' + offerId}`;
    const raw = sessionStorage.getItem(storageKey);
    let storedOffers: { [key: string]: Offer } = raw ? JSON.parse(raw) : {};
  
    if (!storedOffers[offerAliasId + '|' + offerSetId + '|' + offerId]) {
      storedOffers[offerAliasId + '|' + offerSetId + '|' + offerId] = offer;
      sessionStorage.setItem(storageKey, JSON.stringify(storedOffers));
    }
  }

  public removeOfferFromStorageByIds(
    offerAliasId: string,
    offerSetId: string,
    offerId: string
  ): void {
    const storageKey = `offer_byIds_${offerAliasId + '|' + offerSetId + '|' + offerId}`;
    const raw = sessionStorage.getItem(storageKey);
  
    if (!raw) return;
  
    try {
      const storedOffers: { [key: string]: Offer } = JSON.parse(raw);
      const offerKey = `${offerAliasId}|${offerSetId}|${offerId}`;
  
      if (storedOffers[offerKey]) {
        delete storedOffers[offerKey];
  
        // If no more offers remain, remove the entire item
        if (Object.keys(storedOffers).length === 0) {
          sessionStorage.removeItem(storageKey);
        } else {
          sessionStorage.setItem(storageKey, JSON.stringify(storedOffers));
        }
      }
    } catch (err) {
      console.error('Failed to parse or remove offer from sessionStorage:', err);
    }
  }

  public removeAllOffersFromStorageByAliasIdAndCatCd(offerAliasId: string, offerCategory: OfferCategory): void {
    const keysToRemove: string[] = [];
  
    // Loop through all sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith('offer_byIds_')) continue;
  
      const [, compositeKey] = key.split('offer_byIds_');
      const [aliasId, setId, offerId] = compositeKey.split('|');
  
      // Only target keys for the specified aliasId
      if (aliasId !== offerAliasId) continue;
  
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
  
      try {
        const storedOffers: { [key: string]: Offer } = JSON.parse(raw);
        let modified = false;
  
        for (const offerKey in storedOffers) {
          const offer = storedOffers[offerKey];
          if (this.offerCategoryService.isOfferInCategory(offer, offerCategory)) {
            delete storedOffers[offerKey];
            modified = true;
          }
        }
  
        if (modified) {
          if (Object.keys(storedOffers).length === 0) {
            keysToRemove.push(key); // mark for removal
          } else {
            sessionStorage.setItem(key, JSON.stringify(storedOffers));
          }
        }
      } catch (e) {
        console.error(`Error parsing or modifying storage for key ${key}`, e);
      }
    }
  
    // Remove empty keys
    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
    }
  }

  public removeAllRoomOffersFromStorageByAliasId(offerAliasId: string): void {
    const keysToRemove: string[] = [];
  
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith('offer_byIds_')) continue;
  
      const [, compositeKey] = key.split('offer_byIds_');
      const [aliasId, setId, offerId] = compositeKey.split('|');
  
      if (aliasId !== offerAliasId) continue;
  
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
  
      try {
        const storedOffers: { [key: string]: Offer } = JSON.parse(raw);
        let modified = false;
  
        for (const offerKey in storedOffers) {
          const offer = storedOffers[offerKey];
          if (this.offerCategoryService.isOfferInCategory(offer, 'ROOM')) {
            delete storedOffers[offerKey];
            modified = true;
          }
        }
  
        if (modified) {
          if (Object.keys(storedOffers).length === 0) {
            keysToRemove.push(key);
          } else {
            sessionStorage.setItem(key, JSON.stringify(storedOffers));
          }
        }
      } catch (e) {
        console.error(`Error parsing or modifying storage for key ${key}`, e);
      }
    }
  
    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
    }
  }

  public getAllOffersFromSessionStorageByAliasId(): { [offerAliasId: string]: { offerSetId: string; offerId: string; offer: Offer; }[] } {
    
    const results: { [offerAliasId: string]: { offerSetId: string; offerId: string; offer: Offer; }[] } = {};
    const prefix = 'offer_byIds_';
  
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
  
      const keySuffix = key.substring(prefix.length);
      const [aliasId, setId, id] = keySuffix.split('|');
  
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      
      try {
        const parsed: { [key: string]: Offer } = JSON.parse(raw);
        const offer = parsed[`${aliasId}|${setId}|${id}`];
        if (offer) {
          if (!results[aliasId]) {
            results[aliasId] = [];
          }
  
          results[aliasId].push({
            offerSetId: setId,
            offerId: id,
            offer,
          });
        }
      } catch (e) {
        console.error(`Failed to parse offer for key ${key}:`, e);
      }
    }

    return results;
  }
  

  public getAllOffersForOfferAliasId(offerAliasId: string): { offerSetId: string; offerId: string; offer: Offer; }[] {
    const results: { offerSetId: string; offerId: string; offer: Offer; }[] = [];
  
    const prefix = 'offer_byIds_';
  
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
  
      const keySuffix = key.substring(prefix.length);
      const [aliasId, setId, id] = keySuffix.split('|');
  
      if (aliasId !== offerAliasId) continue;
  
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
  
      try {
        const parsed: { [key: string]: Offer } = JSON.parse(raw);
        const offer = parsed[`${aliasId}|${setId}|${id}`];
        if (offer) {
          results.push({
            offerSetId: setId,
            offerId: id,
            offer,
          });
        }
      } catch (e) {
        console.error(`Failed to parse offer for key ${key}:`, e);
      }
    }
  
    return results;
  }


  public setAllSelectedOffersFromSessionStorage() {
      let selectedOffers = this.getAllOffersFromSessionStorageByAliasId();
      if(selectedOffers && selectedOffers !== undefined) {
        this.selectedOffers = selectedOffers;
      }     
  }

  addOfferToCart(offer: Offer, offerAliasId: string, flg_addon: boolean) {
    const offerCategory = this.offerCategoryService.getOfferCategory(offer);

    if (flg_addon && offerCategory !== 'ANCILLARY') {
      this.removeAllOffersInShoppingCartByCatCd(offerAliasId, offerCategory);
      this.removeAllOffersFromStorageByAliasIdAndCatCd(offerAliasId, offerCategory);
    } else if (!flg_addon && offerCategory !== 'ANCILLARY') {
      this.removeAllRoomOffersInShoppingCartByAlias(offerAliasId);
      this.removeAllRoomOffersFromStorageByAliasId(offerAliasId);
    }

    // Prevent adding duplicate offers to cart, especially applicable for the ANCILLARY offer category 
    if (this.checkIfOfferInCart(offerAliasId, offer.offer_set_id, offer.offer_id)) {
      return;
    }

    this.addOffer(offerAliasId, offer?.offer_set_id || '', offer?.offer_id || '', offer!);
    this.addOfferToStorageByIds(offerAliasId, offer?.offer_set_id || '', offer?.offer_id || '', offer!);
  }

  removeOfferFromCart(offer: Offer, offerAliasId: string) {
    this.removeOffer(offerAliasId, offer?.offer_set_id || '', offer?.offer_id || '');
    this.removeOfferFromStorageByIds(offerAliasId, offer?.offer_set_id || '', offer?.offer_id || '');
  }

  emptyCart(offerAliasId: string) {
    if(this.selectedOffers[offerAliasId]) {
      this.removeOffersByOfferAliasId(offerAliasId);
      this.removeAllRoomOffersFromStorageByAliasId(offerAliasId);
    }
  }

  checkIfOfferInCart(offerAliasId: string, offerSetId: string, offerId: string): boolean {
    const offer = this.getOfferByIds(
      offerAliasId,
      offerSetId || '', 
      offerId || ''
    )
    
    if(offer !== null) {
      return true;
    }
    
    return false;
  }

  public calculateTotalQuantity(offers: any[]): number {
    if (!offers || offers.length === 0) return 0;
    return offers.reduce((sum, o) => sum + this.calculateQuantity(o), 0);
  }

  public calculateTotalPrice(offers: any[]): number {
    if (!offers || offers.length === 0) return 0;
    return offers.reduce((sum, o) => sum + this.calculatePrice(o), 0);
  }

  public calculateTotalTaxes(offers: any[]): number {
    if (!offers || offers.length === 0) return 0;
    const total = offers.reduce((sum, o) => {
      return sum + o.ofr_taxes;
    }, 0);

    return total;
  }

  public calculateQuantity(offer: any): number {
    if (!offer) return 0;
    return offer.ofr_quantity ?? 0;
  }

  public calculatePrice(offer: any): number {
    if (!offer) return 0;
    return offer?.net_ofr_price ?? 0;
  }

  private modalInitialState(componentName: string, 
    offerAliasId: string, propertyId: string): any {

    if(componentName === 'OfferPhotoTermsComponent') {
      
      let offers: Offer[] = [];
      
      this.selectedOffers[offerAliasId].forEach((ofr: any) => {
        offers.push(ofr.offer);
      });

      return {
        ...this.sharedService.config,
        initialState: {
          offer: this.selectedOffers[offerAliasId][0].offer,
          offers: offers,
          offerAliasId: offerAliasId,
          propertyId: propertyId
        }
      };
    } else if(componentName === 'ShoppingCartListModalComponent') {
      return {
        ...this.sharedService.config,
        initialState: {
          offerAliasId: offerAliasId,
          propertyId: propertyId,
          selectedOffers: this.selectedOffers[offerAliasId] || undefined
        }
      }
    }
    else {
      return {...this.sharedService.config}
    }
  }

  public openCheckOutOfferTermsModal(
    offerAliasId: string,
    propertyId: string,
    modalComponent: Type<unknown> = OfferPhotoTermsComponent,
  ) {
    const config = this.modalInitialState('OfferPhotoTermsComponent', offerAliasId, propertyId);
    this.modalService.show(modalComponent, config);

  }

  public openShoppingCartListModal(offerAliasId: string, propertyId: string) {
    const config = this.modalInitialState('ShoppingCartListModalComponent', offerAliasId, propertyId);
    this.modalService.show(ShoppingCartListModalComponent, config);
  }

}
